import { normalizeToWords } from "@cuekards/aligner";

/**
 * Voice commands: the vocabulary, the wake-word gate, the script-collision check
 * and the phrase matcher (ticket 15, FR-54 to FR-56).
 *
 * The recogniser is already running to follow the script, so hearing "cuekards
 * stop" costs almost nothing extra. The *risk* is what this module is mostly
 * about: a false trigger mid-script ruins a take that cannot be reshot, and the
 * PRD's R4 answers that with three defences, all of them here.
 *
 * 1. **A wake word.** No command word means anything unless the wake word came
 *    immediately before it, recently (this file).
 * 2. **Script-collision rejection.** If the script itself contains a phrase that
 *    would trigger a command, voice commands are off for that script and the user
 *    is told which phrase did it ({@link findScriptCollision}, FR-55).
 * 3. **Spoken confirmation before stopping** ({@link STOP_CONFIRM_MS}, FR-56),
 *    enforced by `VoiceCommandController`.
 *
 * Everything here is pure — words in, commands out — so all three defences are
 * unit-testable without a microphone, a model or a window. What to *do* about a
 * recognised command is the controller's business.
 */

/** Every transport action a voice command can ask for, plus the confirmation
 * that answers a spoken stop (FR-56). */
export type VoiceCommand =
  | "start"
  | "pause"
  | "resume"
  | "stop"
  | "next"
  | "back"
  | "confirm";

/** The wake word as the PRD spells it and as the user is told to say it. */
export const WAKE_WORD = "cuekards";

/**
 * The wake word as a recogniser will actually transcribe it, normalised.
 *
 * The streaming model decodes into an English sub-word vocabulary with no
 * "cuekards" in it, so said aloud the name comes back as the two words it is
 * spelled from — which is the point of spelling it that way, and why this list
 * is three entries where an invented word needed five. The name is already what
 * the recogniser hears; there is nothing left to compensate for.
 *
 * Every entry is the whole name or a two-word phrase, and each is here for its
 * own reason:
 *
 * - `cue cards` — what a recogniser actually produces, and the form that will
 *   fire in practice.
 * - `cuekards` — the literal spelling, for a decoder whose vocabulary has it.
 * - `queue cards` — the homophone, the other way an English decoder can spell
 *   the same sound.
 *
 * A *fragment* of the name is not a wake form. "kards" was here to catch a
 * partial decode of an invented word; against this name it reaches nothing
 * "cue cards" does not, while narrowing the gate to a single token. "cards"
 * alone is out for the harder version of the same reason: a single common noun
 * as the gate would be a false trigger waiting to happen, which is the one
 * failure this whole module exists to prevent.
 */
export const WAKE_FORMS: readonly (readonly string[])[] = [
  ["cuekards"],
  ["cue", "cards"],
  ["queue", "cards"],
];

/** One command and the words that name it, *after* the wake word. */
export interface VoiceCommandPhrase {
  command: VoiceCommand;
  words: readonly string[];
}

/**
 * The command vocabulary (PRD §7.5's default bindings).
 *
 * Each is a single common word, because the recogniser is biased toward the
 * *script's* vocabulary (FR-38) and anything longer or rarer would be decoded
 * less reliably than the words around it. The wake word is what makes single
 * words safe.
 *
 * `confirm` is not a transport action; it is the answer to a spoken stop
 * (FR-56). It gets its own word rather than reusing "stop" so that two identical
 * false triggers in a row cannot end a take between them.
 */
export const VOICE_COMMAND_PHRASES: readonly VoiceCommandPhrase[] = [
  { command: "start", words: ["start"] },
  { command: "pause", words: ["pause"] },
  { command: "resume", words: ["resume"] },
  { command: "stop", words: ["stop"] },
  { command: "next", words: ["next"] },
  { command: "back", words: ["back"] },
  { command: "confirm", words: ["confirm"] },
];

/** What the user says to confirm a spoken stop, for the prompt on screen. */
export const CONFIRM_PHRASE = `${WAKE_WORD} confirm`;

/**
 * The transport phrases, in the order the PRD's binding table lists them — the
 * answer to "what could I say?" that every voice panel draws.
 *
 * The confirmation is deliberately not among them: it answers a stop rather than
 * giving an order, and it is shown where it is needed instead. Here rather than
 * filtered at each panel because two hosts had written the same filter, and a
 * list of *what the product responds to* is exactly the kind of thing that must
 * not be able to differ between two screens of it.
 */
export const TRANSPORT_COMMAND_PHRASES: readonly VoiceCommandPhrase[] =
  VOICE_COMMAND_PHRASES.filter((phrase) => phrase.command !== "confirm");

/**
 * The question a spoken stop puts on the prompter (FR-56).
 *
 * Here rather than in each prompter for the reason `prompter-chrome.tsx` gives
 * about its own split: two sets of wording about a take that is about to end is
 * the drift this is meant to prevent. There are three prompters now — the
 * desktop's shell, the web's overlay and the phone's — and the phone can import
 * neither of the others, so this file is the only place all three can read one
 * sentence from.
 */
export const STOP_CONFIRM_QUESTION = "Stop recording?";

/**
 * What the switch that turns voice commands on is called (FR-54).
 *
 * Named for what turning it on costs rather than for what it does: the feature
 * is a convenience and the risk is the part worth reading. Shared for the same
 * reason the collision sentence is — a reader who sets this up on the desktop
 * and again on their phone is entitled to the same sentence twice.
 */
export const VOICE_SWITCH_LABEL = `Listen for “${WAKE_WORD} …” while the microphone is open`;

/**
 * How long a spoken stop waits for its confirmation (FR-56).
 *
 * Five seconds is the spec's number and about right: long enough to say three
 * syllables after hearing the prompt, short enough that a stop you changed your
 * mind about has expired before you have finished the sentence you went back to.
 */
export const STOP_CONFIRM_MS = 5_000;

/**
 * The longest silence allowed between the wake word and the command it arms.
 *
 * Without it the wake word would arm forever: say "cue cards" in passing at the
 * top of a ten-minute take, and the word "back" forty lines later would nudge the
 * prompter. Two seconds is comfortably longer than the pause inside "cuekards …
 * stop" and far shorter than the gap between two unrelated sentences.
 */
export const WAKE_GAP_MS = 2_000;

/**
 * Every full phrase that triggers a command — a wake form followed by a command
 * word — longest first.
 *
 * Longest-first is what makes matching unambiguous: "cue cards next" and the
 * shorter forms can both end at the same word, and the longer reading is the one
 * that was actually said.
 */
export const VOICE_TRIGGERS: readonly VoiceCommandPhrase[] =
  VOICE_COMMAND_PHRASES.flatMap((phrase) =>
    WAKE_FORMS.map((wake) => ({
      command: phrase.command,
      words: [...wake, ...phrase.words],
    })),
  ).sort((a, b) => b.words.length - a.words.length);

/** The longest trigger, which is all the matcher ever needs to remember. */
const MAX_TRIGGER_WORDS = Math.max(...VOICE_TRIGGERS.map((t) => t.words.length));

/** Whether `words` ends with `suffix`. */
function endsWith(
  words: readonly { word: string }[],
  suffix: readonly string[],
): boolean {
  if (words.length < suffix.length) return false;
  const offset = words.length - suffix.length;
  return suffix.every((expected, i) => words[offset + i]!.word === expected);
}

/**
 * Watches a stream of recognised words and reports the commands it hears.
 *
 * Fed the recogniser's own output, normalised into the same word form the
 * aligner uses, so "CUEKARDS," and "cuekards" are the same word. Time is the
 * recording's audio clock rather than a wall clock, for the same reason
 * `FollowerController` counts frames: the only "now" that means anything here is
 * how much audio has been heard.
 */
export interface VoiceCommandMatcher {
  /**
   * Consume the words the recogniser produced at `atMs` and return the commands
   * they completed, in order. Usually empty.
   */
  hear(words: readonly string[], atMs: number): VoiceCommand[];
  /** Forget the words heard so far — a new take, or the microphone closing. */
  reset(): void;
}

export function createVoiceCommandMatcher(options?: {
  /** Override the wake-word timeout, for tests. */
  wakeGapMs?: number;
}): VoiceCommandMatcher {
  const wakeGapMs = options?.wakeGapMs ?? WAKE_GAP_MS;
  /** The tail of the heard-word stream, no longer than the longest trigger. */
  let recent: { word: string; atMs: number }[] = [];

  return {
    hear(words: readonly string[], atMs: number): VoiceCommand[] {
      const commands: VoiceCommand[] = [];
      // Joined and re-split so a batch arrives through exactly the same
      // normalisation the script index was built with (FR-15).
      for (const word of normalizeToWords(words.join(" "))) {
        recent.push({ word, atMs });
        if (recent.length > MAX_TRIGGER_WORDS) recent.shift();

        const hit = VOICE_TRIGGERS.find(
          (trigger) =>
            endsWith(recent, trigger.words) &&
            // Measured from the wake word, so an armed command that arrives
            // after a long pause is not one the user meant to give.
            atMs - recent[recent.length - trigger.words.length]!.atMs <=
              wakeGapMs,
        );
        if (!hit) continue;
        commands.push(hit.command);
        // The words are spent: they must not also form the tail of a second
        // match against whatever is said next.
        recent = [];
      }
      return commands;
    },

    reset(): void {
      recent = [];
    },
  };
}

/** A voice-command phrase found in the loaded script (FR-55). */
export interface VoiceCollision {
  /** The command the reader would trigger by reading the line aloud. */
  command: VoiceCommand;
  /** The colliding words as they appear in the spoken-word stream. */
  phrase: string;
}

/**
 * The first voice-command phrase the script would make the reader say, or null
 * when there is none (FR-55).
 *
 * Matched against the script's *spoken* word stream, normalised, with sentence
 * boundaries and punctuation gone — because that is exactly what the matcher
 * hears. "I wrote these on cue cards. Next up…" is two sentences on the page and
 * three consecutive words in the microphone.
 *
 * Director notes are included in the check even though they are excluded from
 * the alignment index (FR-14). A note is on screen in front of someone reading
 * aloud, and the cost of being wrong in each direction is not symmetric: an
 * over-eager collision costs the user the voice commands for one script, while a
 * missed one costs a take.
 */
export function findScriptCollision(scriptText: string): VoiceCollision | null {
  const words = normalizeToWords(scriptText);
  for (let i = 0; i < words.length; i++) {
    for (const trigger of VOICE_TRIGGERS) {
      if (i + trigger.words.length > words.length) continue;
      const matches = trigger.words.every(
        (expected, offset) => words[i + offset] === expected,
      );
      if (matches) {
        return { command: trigger.command, phrase: trigger.words.join(" ") };
      }
    }
  }
  return null;
}

/** Why voice commands are off for this script, phrased so the user can see what
 * to change (FR-55). */
export function voiceCollisionMessage(collision: VoiceCollision): string {
  return (
    `Voice commands are off for this script: it contains the phrase ` +
    `“${collision.phrase}”, which would trigger the ${collision.command} ` +
    `command if you read it aloud. Reword that line to use them.`
  );
}

/**
 * The voice-command state both windows are views onto.
 *
 * Separate from `FollowerState` because the two answer different questions: the
 * follower is about whether the prompter can track you, this is about whether the
 * app is listening for orders. A follower that is running perfectly still has
 * voice commands off unless they were asked for.
 */
export interface VoiceCommandState {
  /** Whether the user has turned voice commands on (FR-54). Off by default and
   * across restarts until explicitly enabled. */
  enabled: boolean;
  /** Why commands are not armed for the loaded script, if they are not (FR-55).
   * Reported whether or not commands are enabled, so the reason the toggle will
   * not arm is already on the state when the user reaches for it. */
  collision?: string;
  /** Whether a spoken stop is waiting for its confirmation, and until when in
   * the audio clock. The overlay shows this: a confirmation you cannot see being
   * asked for is one you will not give (FR-56). */
  awaitingConfirmation: boolean;
  /** The last command acted on, so the control window can show that a phrase was
   * heard rather than leaving the user guessing. */
  lastCommand?: VoiceCommand;
}

/** Whether commands are actually being listened for: on, and no phrase in the
 * script would trigger one. */
export function voiceCommandsArmed(state: VoiceCommandState): boolean {
  return state.enabled && state.collision === undefined;
}

/** The state before anything has been loaded or enabled. */
export const IDLE_VOICE_COMMANDS: VoiceCommandState = {
  enabled: false,
  awaitingConfirmation: false,
};

/** How voice commands read in the control window. */
export function voiceCommandStatusLabel(state: VoiceCommandState): string {
  if (!state.enabled) return "Off";
  if (!voiceCommandsArmed(state)) return "Off for this script";
  if (state.awaitingConfirmation) return "Confirm stop?";
  return `Listening for “${WAKE_WORD} …”`;
}
